import React, { useState, useEffect, useContext, useCallback } from "react";
import { Card, Pagination, Icon, CircularProgress } from "@mui/material";
import Swal from "sweetalert2";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import { LanguageContext } from "App";
import SessionsApi from "../../../Classes/API/Administration/SessionsApi";
import AccountingApi from "Classes/API/AccountingApi";
import SearchStringOrIntReq from "../../../Classes/Models/Requests/SearchStringOrIntReq";
import SearchTwoIntReq from "../../../Classes/Models/Requests/SearchTwoIntReq";

const UnlinkedSessionsTable = ({ Contract, setIsLoaded }) => {
  const { translations } = useContext(LanguageContext);
  const [tableRows, setTableRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 1,
    current: 1,
    pageSize: 5,
  });

  const columns = [
    { Header: "Session Name", accessor: "name", width: "75%", align: "left" },
    { Header: "Action", accessor: "action", width: "50%", align: "left" },
  ];

  const getUnlinkedSessions = useCallback(async () => {
    setLoading(true);
    let req = new SearchStringOrIntReq();
    req.pagingParams.pageNumber = pagination.current;
    req.pagingParams.pageSize = pagination.pageSize;
    req.SearchInt = Contract.id;

    const res = await AccountingApi.GetUnlinkedSessions(req);

    if (res.status === "Success") {
      const rows = res.data.list.map((session) => ({
        name: (
          <MDBox display="flex" alignItems="center">
            <Icon sx={{ mr: 1, color: "info.main", fontSize: "small" }}>
              assignment
            </Icon>
            <MDTypography variant="button" fontWeight="medium" color="dark">
              {session.name}
            </MDTypography>
          </MDBox>
        ),
        action: (
          <MDBox>
            <MDButton
              variant="gradient"
              color="info"
              size="small"
              circular
              onClick={() => handleAddSession(session)}
            >
              <Icon sx={{ fontWeight: "bold", mr: 1 }}>link</Icon>
              &nbsp;Link Session
            </MDButton>
          </MDBox>
        ),
      }));

      setTableRows(rows);
      setPagination((prev) => ({
        ...prev,
        total: Math.ceil(res.data.totalItems / prev.pageSize),
      }));
    } else {
      Swal.fire("Error", res.message, "error");
    }
    setLoading(false);
  }, [pagination.current, Contract.id]);

  const handleAddSession = async (session) => {
    let req = new SearchTwoIntReq();
    req.ParentSessionId = session.id;
    req.ContractId = Contract.id;

    const res = await SessionsApi.CreateChildSession(req);
    if (res.status === "Success") {
      Swal.fire({
        title: "Linked!",
        text: `Session "${session.name}" linked successfully.`,
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
      setIsLoaded(false);
      getUnlinkedSessions();
    } else {
      Swal.fire("Error", res.message, "error");
    }
  };

  useEffect(() => {
    getUnlinkedSessions();
  }, [getUnlinkedSessions]);

  return (
    <Card className="mt-5" sx={{ overflow: "visible", boxShadow: 3 }}>
      <MDBox
        variant="gradient"
        bgColor="info"
        coloredShadow="info"
        p={2}
        mt={-3}
        mb={1}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          borderTopLeftRadius: "1.1rem",
          borderTopRightRadius: "1.1rem",
        }}
      >
        <MDTypography variant="h6" color="white" fontWeight="medium">
          Unlinked Contract Sessions
        </MDTypography>
        <MDTypography variant="caption" color="white">
          Total Items: {pagination.total * pagination.pageSize}
        </MDTypography>
      </MDBox>

      <MDBox pt={2}>
        {loading ? (
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress color="info" size={30} />
          </MDBox>
        ) : tableRows.length > 0 ? (
          <DataTable
            table={{ columns, rows: tableRows }}
            isSorted={false}
            entriesPerPage={false}
            showTotalEntries={false}
            noEndBorder
          />
        ) : (
          <MDBox textAlign="center" py={10}>
            <Icon fontSize="large" color="disabled">
              event_busy
            </Icon>
            <MDTypography
              variant="h6"
              color="secondary"
              sx={{ mt: 1, opacity: 0.7 }}
            >
              No sessions available to link
            </MDTypography>
          </MDBox>
        )}
      </MDBox>

      {tableRows.length > 0 && (
        <MDBox
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          p={3}
        >
          <Pagination
            count={pagination.total}
            page={pagination.current}
            color="info"
            onChange={(e, val) =>
              setPagination((p) => ({ ...p, current: val }))
            }
            size="small"
          />
        </MDBox>
      )}
    </Card>
  );
};

export default UnlinkedSessionsTable;
